import type { PostgrestError } from '@supabase/supabase-js';
import type { AppUser } from '@/lib/auth';
import { USER_ROLES } from '@/lib/auth/user';
import { requireAdminOrManager } from '@/lib/auth/guards';
import {
  createNotFoundError,
  createServiceError,
  fromPostgrestError,
  isServiceError,
  normalizeError,
} from '@/lib/services/errors';
import { supabase } from '@/lib/supabase/client';
import type { PaginatedResult, PaginationInput } from '@/lib/types/property';
import { normalizePhoneNumber } from '@/lib/utils/phone';
import type {
  LeadDetail,
  LeadFormValues,
  LeadLandingPayload,
  LeadInsert,
  LeadListFilters,
  LeadRow,
  LeadSummary,
  LeadTimelineEvent,
  LeadTimelineEventType,
  LeadUpdate,
} from '@/lib/types/lead';

const client = supabase;

const TIMELINE_ORIGINS = {
  publicCapture: 'public.submit_lead_public',
  adminCapture: 'leads.create.admin',
  stageChange: 'leads.update.stage',
  assignment: 'leads.update.assignment',
  note: 'leads.update.note',
  followUpSet: 'leads.follow_up.set',
  followUpCleared: 'leads.follow_up.clear',
  contactLogged: 'leads.contact.log',
} as const;

export const LEADS_DEFAULT_PAGE = 1;
export const LEADS_DEFAULT_PAGE_SIZE = 25;
export const LEADS_MAX_PAGE_SIZE = 200;

export interface ListLeadsArgs {
  user: AppUser | null;
  filters?: LeadListFilters;
  page?: number;
  pageSize?: number;
  signal?: AbortSignal;
}

export interface ListLeadsByPhoneArgs {
  user: AppUser | null;
  phone: string | null;
  limit?: number;
  signal?: AbortSignal;
}

export interface ListLeadTimelineEventsArgs {
  user: AppUser | null;
  leadId: string;
  limit?: number;
}

export interface GetLeadDetailArgs {
  user: AppUser | null;
  leadId: string;
}

export interface CreateLeadArgs {
  user: AppUser | null;
  values: LeadFormValues;
}

export interface UpdateLeadArgs {
  user: AppUser | null;
  leadId: string;
  values: LeadFormValues;
}

export interface SubmitLeadArgs {
  values: LeadLandingPayload;
}

export interface ScheduleLeadFollowUpArgs {
  user: AppUser | null;
  leadId: string;
  nextFollowUpAt: string | null;
}

export interface LogLeadContactArgs {
  user: AppUser | null;
  leadId: string;
  timestamp?: string | null;
  nextFollowUpAt?: string | null;
}

export async function listLeadsForUser({
  user,
  filters,
  page,
  pageSize,
  signal,
}: ListLeadsArgs): Promise<PaginatedResult<LeadSummary>> {
  const actor = requireAdminOrManager(user);
  void actor;

  const pagination = normalizePagination(page, pageSize);
  const from = (pagination.page - 1) * pagination.pageSize;
  const to = from + pagination.pageSize - 1;

  try {
    let query = createLeadsQuery().select('*', { count: 'exact' });
    query = applyLeadListFilters(query, filters);
    query = query.order('created_at', { ascending: false });

    if (signal) {
      query = query.abortSignal(signal);
    }

    const { data, error, count } = await query.range(from, to);

    if (error !== null) {
      throw fromPostgrestError(error);
    }

    const rows = (Array.isArray(data) ? data : []) as LeadRow[];
    const items = rows.map(mapLeadRowToSummary);
    const totalItems = typeof count === 'number' ? count : items.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pagination.pageSize));

    return {
      items,
      totalItems,
      totalPages,
      page: pagination.page,
      pageSize: pagination.pageSize,
    };
  } catch (error) {
    handleLeadServiceError(error);
  }
}

export async function listLeadsByPhoneForUser({
  user,
  phone,
  limit,
  signal,
}: ListLeadsByPhoneArgs): Promise<LeadSummary[]> {
  const actor = requireAdminOrManager(user);
  void actor;

  const normalizedPhone = normalizePhoneNumber(phone);
  if (!normalizedPhone || normalizedPhone.length < 7) {
    return [];
  }

  const maxLimit =
    typeof limit === 'number' && Number.isFinite(limit)
      ? Math.min(Math.max(Math.trunc(limit), 1), LEADS_MAX_PAGE_SIZE)
      : 50;

  try {
    let query = createLeadsQuery()
      .select('*')
      .ilike('phone', buildPhoneIlikePattern(normalizedPhone))
      .order('created_at', { ascending: false })
      .limit(maxLimit);

    if (signal) {
      query = query.abortSignal(signal);
    }

    const { data, error } = await query;

    if (error !== null) {
      throw fromPostgrestError(error);
    }

    const rows = (Array.isArray(data) ? data : []) as LeadRow[];
    return rows.map(mapLeadRowToSummary);
  } catch (error) {
    handleLeadServiceError(error);
  }
}

export async function listLeadTimelineEventsForUser({
  user,
  leadId,
  limit,
}: ListLeadTimelineEventsArgs): Promise<LeadTimelineEvent[]> {
  const actor = requireAdminOrManager(user);
  void actor;

  const normalizedLeadId = leadId.trim();
  if (normalizedLeadId.length === 0) {
    throw createServiceError({
      code: 'validation_failed',
      message: 'Lead identifier is required.',
    });
  }

  try {
    const { data, error } = await createLeadTimelineEventsQuery()
      .select(
        'id, lead_id, event_type, payload, recorded_at, recorded_by, recorded_by_profile:profiles!lead_timeline_events_recorded_by_fkey(display_name)',
      )
      .eq('lead_id', normalizedLeadId)
      .order('recorded_at', { ascending: false })
      .limit(typeof limit === 'number' && Number.isFinite(limit) ? Math.max(limit, 1) : 100);

    if (error !== null) {
      throw fromPostgrestError(error);
    }

    const rows = (Array.isArray(data) ? data : []) as LeadTimelineQueryRow[];

    return rows.map((row) => ({
      id: row.id,
      leadId: row.lead_id,
      eventType: row.event_type,
      payload: row.payload ?? {},
      recordedAt: row.recorded_at,
      recordedBy: row.recorded_by,
      recordedByDisplayName: Array.isArray(row.recorded_by_profile)
        ? (row.recorded_by_profile.at(0)?.display_name ?? null)
        : (row.recorded_by_profile?.display_name ?? null),
    }));
  } catch (error) {
    handleLeadServiceError(error);
  }
}

export async function getLeadDetailForUser({
  user,
  leadId,
}: GetLeadDetailArgs): Promise<LeadDetail> {
  const actor = requireAdminOrManager(user);
  void actor;

  try {
    const { data, error } = await createLeadsQuery().select('*').eq('id', leadId).maybeSingle();

    if (error !== null) {
      throw fromPostgrestError(error);
    }

    const record = data as LeadRow | null;

    if (record === null) {
      throw createNotFoundError('Lead not found.');
    }

    return mapLeadRowToDetail(record);
  } catch (error) {
    handleLeadServiceError(error);
  }
}

export async function createLeadForUser({ user, values }: CreateLeadArgs): Promise<LeadDetail> {
  const actor = requireAdminOrManager(user);
  void actor;

  try {
    const payload = mapLeadFormValuesToInsert(values);
    const { data, error } = await createLeadsQuery().insert(payload).select('*').maybeSingle();

    if (error !== null) {
      throw fromPostgrestError(error);
    }

    const record = data as LeadRow | null;

    if (record === null) {
      throw createServiceError({ code: 'unexpected', message: 'Failed to create lead.' });
    }

    await recordLeadTimelineEventSafe(
      {
        user,
        leadId: record.id,
        eventType: 'captured',
        payload: createTimelinePayload(
          {
            stage: record.stage,
            campaignSource: record.campaign_source,
            campaignMedium: record.campaign_medium,
          },
          TIMELINE_ORIGINS.adminCapture,
        ),
      },
      'admin_create',
    );

    return mapLeadRowToDetail(record);
  } catch (error) {
    handleLeadServiceError(error);
  }
}

export async function submitLeadFromLanding({ values }: SubmitLeadArgs): Promise<LeadDetail> {
  try {
    const nameParts = deriveLeadNameParts({
      fullName: values.fullName,
      firstName: values.firstName,
      lastName: values.lastName,
    });

    const payload: LeadInsert = {
      full_name: values.fullName.trim(),
      first_name: nameParts.firstName,
      last_name: nameParts.lastName,
      email: values.email.trim().toLowerCase(),
      phone: normalizePhoneNumber(values.phone),
      campaign_source: normalizeNullableString(values.campaignSource),
      campaign_medium: normalizeNullableString(values.campaignMedium),
      desired_property_id: normalizeNullableString(values.desiredPropertyId),
      desired_unit_id: normalizeNullableString(values.desiredUnitId),
      budget_monthly: normalizeNullableNumber(values.budgetMonthly),
      preferred_move_in_date: normalizeNullableDate(values.preferredMoveInDate),
      notes: normalizeNullableStringPreserveWhitespace(values.notes),
      stage: 'new',
    };

    const { data, error } = await (
      client.rpc as unknown as (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: PostgrestError | null }>
    )('submit_lead_public', {
      p_full_name: payload.full_name,
      p_first_name: payload.first_name,
      p_last_name: payload.last_name,
      p_email: payload.email,
      p_phone: payload.phone,
      p_campaign_source: payload.campaign_source,
      p_campaign_medium: payload.campaign_medium,
      p_desired_property_id: payload.desired_property_id,
      p_desired_unit_id: payload.desired_unit_id,
      p_budget_monthly: payload.budget_monthly,
      p_preferred_move_in_date: payload.preferred_move_in_date,
      p_notes: payload.notes,
    });

    if (error !== null) {
      throw fromPostgrestError(error);
    }

    const record = data as LeadRow | null;

    if (record === null) {
      throw createServiceError({ code: 'unexpected', message: 'Lead submission failed.' });
    }

    return mapLeadRowToDetail(record);
  } catch (error) {
    handleLeadServiceError(error);
  }
}

export async function scheduleLeadFollowUpForUser({
  user,
  leadId,
  nextFollowUpAt,
}: ScheduleLeadFollowUpArgs): Promise<LeadDetail> {
  const actor = requireAdminOrManager(user);
  void actor;

  try {
    const { data: existingData, error: existingError } = await createLeadsQuery()
      .select('*')
      .eq('id', leadId)
      .maybeSingle();

    if (existingError !== null) {
      throw fromPostgrestError(existingError);
    }

    const existing = existingData as LeadRow | null;

    if (existing === null) {
      throw createNotFoundError('Lead not found.');
    }

    ensureManagerCanMutateLead(user, existing);

    const payload = {
      next_follow_up_at: normalizeNullableDateTime(nextFollowUpAt),
    } satisfies Partial<LeadUpdate>;

    const { data, error } = await createLeadsQuery()
      .update(payload)
      .eq('id', leadId)
      .select('*')
      .maybeSingle();

    if (error !== null) {
      throw fromPostgrestError(error);
    }

    const record = data as LeadRow | null;

    if (record === null) {
      throw createNotFoundError('Lead not found.');
    }

    const previousNextFollowUpAt = toIsoString(existing.next_follow_up_at);
    const updatedNextFollowUpAt = toIsoString(record.next_follow_up_at);

    if (previousNextFollowUpAt !== updatedNextFollowUpAt) {
      const nextEventType: LeadTimelineEventType =
        updatedNextFollowUpAt !== null ? 'follow_up_set' : 'follow_up_cleared';
      const origin =
        nextEventType === 'follow_up_set'
          ? TIMELINE_ORIGINS.followUpSet
          : TIMELINE_ORIGINS.followUpCleared;

      await recordLeadTimelineEventSafe(
        {
          user,
          leadId,
          eventType: nextEventType,
          payload: createTimelinePayload(
            {
              previousNextFollowUpAt,
              nextFollowUpAt: updatedNextFollowUpAt,
            },
            origin,
          ),
        },
        'follow_up_schedule',
      );
    }

    return mapLeadRowToDetail(record);
  } catch (error) {
    handleLeadServiceError(error);
  }
}

export async function logLeadContactForUser({
  user,
  leadId,
  timestamp,
  nextFollowUpAt,
}: LogLeadContactArgs): Promise<LeadDetail> {
  const actor = requireAdminOrManager(user);
  void actor;

  try {
    const { data: existingData, error: existingError } = await createLeadsQuery()
      .select('*')
      .eq('id', leadId)
      .maybeSingle();

    if (existingError !== null) {
      throw fromPostgrestError(existingError);
    }

    const existing = existingData as LeadRow | null;

    if (existing === null) {
      throw createNotFoundError('Lead not found.');
    }

    ensureManagerCanMutateLead(user, existing);

    const payload = {
      last_contacted_at: normalizeNullableDateTime(timestamp ?? new Date().toISOString()),
      next_follow_up_at: normalizeNullableDateTime(nextFollowUpAt),
    } satisfies Partial<LeadUpdate>;

    const { data, error } = await createLeadsQuery()
      .update(payload)
      .eq('id', leadId)
      .select('*')
      .maybeSingle();

    if (error !== null) {
      throw fromPostgrestError(error);
    }

    const record = data as LeadRow | null;

    if (record === null) {
      throw createNotFoundError('Lead not found.');
    }

    const previousContactedAt = toIsoString(existing.last_contacted_at);
    const previousNextFollowUpAt = toIsoString(existing.next_follow_up_at);
    const updatedContactedAt = toIsoString(record.last_contacted_at);
    const updatedNextFollowUpAt = toIsoString(record.next_follow_up_at);

    if (
      previousContactedAt !== updatedContactedAt ||
      previousNextFollowUpAt !== updatedNextFollowUpAt
    ) {
      await recordLeadTimelineEventSafe(
        {
          user,
          leadId,
          eventType: 'contact_logged',
          payload: createTimelinePayload(
            {
              previousContactedAt,
              contactedAt: updatedContactedAt,
              previousNextFollowUpAt,
              nextFollowUpAt: updatedNextFollowUpAt,
            },
            TIMELINE_ORIGINS.contactLogged,
          ),
        },
        'contact_logged',
      );
    }

    return mapLeadRowToDetail(record);
  } catch (error) {
    handleLeadServiceError(error);
  }
}

export async function updateLeadForUser({
  user,
  leadId,
  values,
}: UpdateLeadArgs): Promise<LeadDetail> {
  const actor = requireAdminOrManager(user);
  void actor;

  try {
    const { data: existingData, error: existingError } = await createLeadsQuery()
      .select('*')
      .eq('id', leadId)
      .maybeSingle();

    if (existingError !== null) {
      throw fromPostgrestError(existingError);
    }

    const existing = existingData as LeadRow | null;

    if (existing === null) {
      throw createNotFoundError('Lead not found.');
    }

    ensureManagerCanMutateLead(user, existing);

    const payload = mapLeadFormValuesToUpdate(values);
    const { data, error } = await createLeadsQuery()
      .update(payload)
      .eq('id', leadId)
      .select('*')
      .maybeSingle();

    if (error !== null) {
      throw fromPostgrestError(error);
    }

    const record = data as LeadRow | null;

    if (record === null) {
      throw createNotFoundError('Lead not found.');
    }

    const timelineTasks: Array<Promise<void>> = [];
    const notesChanged =
      normalizeTextForComparison(existing.notes) !== normalizeTextForComparison(record.notes);

    if (existing.stage !== record.stage) {
      timelineTasks.push(
        recordLeadTimelineEventSafe(
          {
            user,
            leadId,
            eventType: 'stage_change',
            payload: createTimelinePayload(
              {
                previousStage: existing.stage,
                stage: record.stage,
              },
              TIMELINE_ORIGINS.stageChange,
            ),
          },
          'stage_change',
        ),
      );
    }

    if ((existing.assigned_to ?? null) !== (record.assigned_to ?? null)) {
      timelineTasks.push(
        recordLeadTimelineEventSafe(
          {
            user,
            leadId,
            eventType: 'assignment',
            payload: createTimelinePayload(
              {
                previousAssignedTo: existing.assigned_to,
                assignedTo: record.assigned_to,
              },
              TIMELINE_ORIGINS.assignment,
            ),
          },
          'assignment',
        ),
      );
    }

    if (notesChanged) {
      timelineTasks.push(
        recordLeadTimelineEventSafe(
          {
            user,
            leadId,
            eventType: 'note',
            payload: createTimelinePayload(
              {
                previousNotes: existing.notes,
                notes: record.notes,
              },
              TIMELINE_ORIGINS.note,
            ),
          },
          'note',
        ),
      );
    }

    if (timelineTasks.length > 0) {
      await Promise.all(timelineTasks);
    }

    return mapLeadRowToDetail(record);
  } catch (error) {
    handleLeadServiceError(error);
  }
}

function createLeadsQuery() {
  return client.from('leads');
}

function createLeadTimelineEventsQuery() {
  return client.from('lead_timeline_events');
}

function normalizePagination(page?: number, pageSize?: number): PaginationInput {
  const safePage =
    typeof page === 'number' && Number.isFinite(page) && page > 0 ? page : LEADS_DEFAULT_PAGE;
  const safePageSize =
    typeof pageSize === 'number' && Number.isFinite(pageSize) && pageSize > 0
      ? Math.min(Math.trunc(pageSize), LEADS_MAX_PAGE_SIZE)
      : LEADS_DEFAULT_PAGE_SIZE;

  return {
    page: Math.trunc(safePage),
    pageSize: safePageSize,
  };
}

interface LeadFilterOperators<TBuilder> {
  in(column: string, values: readonly unknown[]): TBuilder;
  eq(column: string, value: unknown): TBuilder;
  or(filters: string, options?: { referencedTable?: string }): TBuilder;
}

interface LeadTimelineQueryRow {
  id: string;
  lead_id: string;
  event_type: LeadTimelineEventType;
  payload: Record<string, unknown> | null;
  recorded_at: string;
  recorded_by: string | null;
  recorded_by_profile?:
    | Array<{
        display_name: string | null;
      }>
    | {
        display_name: string | null;
      }
    | null;
}

function applyLeadListFilters<TBuilder extends LeadFilterOperators<TBuilder>>(
  query: TBuilder,
  filters?: LeadListFilters,
): TBuilder {
  if (!filters) {
    return query;
  }

  const {
    stage,
    campaignSource,
    campaignMedium,
    assignedTo,
    desiredPropertyId,
    desiredUnitId,
    searchTerm,
  } = filters;
  let builder = query;

  if (Array.isArray(stage) && stage.length > 0) {
    builder = builder.in('stage', stage);
  }

  if (typeof campaignSource === 'string' && campaignSource.trim().length > 0) {
    builder = builder.eq('campaign_source', campaignSource.trim());
  }

  if (typeof campaignMedium === 'string' && campaignMedium.trim().length > 0) {
    builder = builder.eq('campaign_medium', campaignMedium.trim());
  }

  if (typeof assignedTo === 'string' && assignedTo.trim().length > 0) {
    builder = builder.eq('assigned_to', assignedTo.trim());
  }

  if (typeof desiredPropertyId === 'string' && desiredPropertyId.trim().length > 0) {
    builder = builder.eq('desired_property_id', desiredPropertyId.trim());
  }

  if (typeof desiredUnitId === 'string' && desiredUnitId.trim().length > 0) {
    builder = builder.eq('desired_unit_id', desiredUnitId.trim());
  }

  const normalizedSearchTerm = normalizeSearchTerm(searchTerm);
  if (normalizedSearchTerm !== null) {
    builder = builder.or(
      `full_name.ilike.%${normalizedSearchTerm}%,email.ilike.%${normalizedSearchTerm}%`,
    );
  }

  return builder;
}

function mapLeadRowToSummary(row: LeadRow): LeadSummary {
  return {
    id: row.id,
    fullName: row.full_name,
    email: row.email,
    phone: row.phone ?? null,
    stage: row.stage,
    campaignSource: row.campaign_source ?? null,
    campaignMedium: row.campaign_medium ?? null,
    desiredPropertyId: row.desired_property_id ?? null,
    desiredUnitId: row.desired_unit_id ?? null,
    assignedTo: row.assigned_to ?? null,
    budgetMonthly: row.budget_monthly ?? null,
    preferredMoveInDate: row.preferred_move_in_date ?? null,
    lastContactedAt: row.last_contacted_at ?? null,
    nextFollowUpAt: row.next_follow_up_at ?? null,
    notes: row.notes ?? null,
    pipelineStatus: row.pipeline_status,
    prospectUserId: row.prospect_user_id ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapLeadRowToDetail(row: LeadRow): LeadDetail {
  return {
    ...mapLeadRowToSummary(row),
    notes: row.notes ?? null,
    lossReason: row.loss_reason ?? null,
  };
}

function mapLeadFormValuesToInsert(values: LeadFormValues): LeadInsert {
  const normalized = sanitizeLeadFormValues(values);
  const nameParts = deriveLeadNameParts({
    fullName: normalized.fullName,
  });

  return {
    full_name: normalized.fullName,
    first_name: nameParts.firstName,
    last_name: nameParts.lastName,
    email: normalized.email,
    phone: normalized.phone,
    campaign_source: normalized.campaignSource,
    campaign_medium: normalized.campaignMedium,
    desired_property_id: normalized.desiredPropertyId,
    desired_unit_id: normalized.desiredUnitId,
    stage: normalized.stage,
    assigned_to: normalized.assignedTo,
    budget_monthly: normalized.budgetMonthly,
    preferred_move_in_date: normalized.preferredMoveInDate,
    last_contacted_at: normalized.lastContactedAt,
    next_follow_up_at: normalized.nextFollowUpAt,
    loss_reason: normalized.lossReason,
    notes: normalized.notes,
  };
}

function mapLeadFormValuesToUpdate(values: LeadFormValues): LeadUpdate {
  const normalized = sanitizeLeadFormValues(values);
  const nameParts = deriveLeadNameParts({
    fullName: normalized.fullName,
  });

  return {
    full_name: normalized.fullName,
    first_name: nameParts.firstName,
    last_name: nameParts.lastName,
    email: normalized.email,
    phone: normalized.phone,
    campaign_source: normalized.campaignSource,
    campaign_medium: normalized.campaignMedium,
    desired_property_id: normalized.desiredPropertyId,
    desired_unit_id: normalized.desiredUnitId,
    stage: normalized.stage,
    assigned_to: normalized.assignedTo,
    budget_monthly: normalized.budgetMonthly,
    preferred_move_in_date: normalized.preferredMoveInDate,
    last_contacted_at: normalized.lastContactedAt,
    next_follow_up_at: normalized.nextFollowUpAt,
    loss_reason: normalized.lossReason,
    notes: normalized.notes,
  };
}

function ensureManagerCanMutateLead(user: AppUser | null, lead: LeadRow): void {
  if (!user) {
    throw createServiceError({ code: 'unauthorized', message: 'You must be signed in.' });
  }

  if (user.role === USER_ROLES.manager) {
    const assignedTo = lead.assigned_to ?? null;
    if (assignedTo !== null && assignedTo !== user.id) {
      throw createServiceError({
        code: 'forbidden',
        message: 'Only the assigned manager can update this lead.',
      });
    }
  }
}

interface NormalizedLeadFormValues {
  fullName: string;
  email: string;
  phone: string | null;
  campaignSource: string | null;
  campaignMedium: string | null;
  desiredPropertyId: string | null;
  desiredUnitId: string | null;
  stage: LeadFormValues['stage'];
  assignedTo: string | null;
  budgetMonthly: number | null;
  preferredMoveInDate: string | null;
  lastContactedAt: string | null;
  nextFollowUpAt: string | null;
  notes: string | null;
  lossReason: string | null;
}

function sanitizeLeadFormValues(values: LeadFormValues): NormalizedLeadFormValues {
  return {
    fullName: values.fullName.trim(),
    email: values.email.trim().toLowerCase(),
    phone: normalizePhoneNumber(values.phone),
    campaignSource: normalizeNullableString(values.campaignSource),
    campaignMedium: normalizeNullableString(values.campaignMedium),
    desiredPropertyId: normalizeNullableString(values.desiredPropertyId),
    desiredUnitId: normalizeNullableString(values.desiredUnitId),
    stage: values.stage,
    assignedTo: normalizeNullableString(values.assignedTo),
    budgetMonthly: normalizeNullableNumber(values.budgetMonthly),
    preferredMoveInDate: normalizeNullableDate(values.preferredMoveInDate),
    lastContactedAt: normalizeNullableDateTime(values.lastContactedAt),
    nextFollowUpAt: normalizeNullableDateTime(values.nextFollowUpAt),
    notes: normalizeNullableStringPreserveWhitespace(values.notes),
    lossReason: normalizeNullableString(values.lossReason),
  };
}

function normalizeNullableString(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function buildPhoneIlikePattern(digits: string): string {
  return `%${digits.split('').join('%')}%`;
}

function deriveLeadNameParts(args: {
  fullName: string;
  firstName?: string | null;
  lastName?: string | null;
}): { firstName: string | null; lastName: string | null } {
  const explicitFirstName = normalizeNullableString(args.firstName);
  const explicitLastName = normalizeNullableString(args.lastName);

  const normalizedFullName = args.fullName.trim();
  const tokens = normalizedFullName.split(/\s+/).filter((token) => token.length > 0);
  const derivedFirstName = tokens.length > 0 ? tokens[0] : null;
  const derivedLastName = tokens.length > 1 ? tokens.slice(1).join(' ') : null;

  return {
    firstName: explicitFirstName ?? derivedFirstName,
    lastName: explicitLastName ?? derivedLastName,
  };
}

function normalizeNullableStringPreserveWhitespace(
  value: string | null | undefined,
): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }

  return trimmed;
}

function normalizeNullableNumber(value: number | string | null | undefined): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.round(value);
  }

  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
}

function normalizeNullableDate(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }

  return trimmed;
}

function normalizeNullableDateTime(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const timestamp = Date.parse(trimmed);
  if (Number.isNaN(timestamp)) {
    return null;
  }

  return new Date(timestamp).toISOString();
}

function normalizeSearchTerm(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }

  return escapeForIlike(trimmed);
}

function escapeForIlike(value: string): string {
  return value.replace(/[%_]/g, (match) => `\\${match}`).replace(/,/g, ' ');
}

interface RecordLeadTimelineEventArgs {
  user: AppUser | null;
  leadId: string;
  eventType: LeadTimelineEventType;
  payload?: Record<string, unknown>;
}

function normalizeTimelinePayload(payload?: Record<string, unknown>): Record<string, unknown> {
  if (!payload) {
    return {};
  }

  return Object.entries(payload).reduce<Record<string, unknown>>((acc, [key, value]) => {
    if (value === undefined) {
      return acc;
    }

    if (value instanceof Date) {
      acc[key] = value.toISOString();
      return acc;
    }

    if (Array.isArray(value)) {
      const normalizedArray = value.map<unknown>((item) =>
        item instanceof Date ? item.toISOString() : item,
      );
      acc[key] = normalizedArray;
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {});
}

function createTimelinePayload(
  payload: Record<string, unknown>,
  origin: string,
): Record<string, unknown> {
  return {
    origin,
    ...normalizeTimelinePayload(payload),
  };
}

function toIsoString(value: string | null | undefined): string | null {
  return normalizeNullableDateTime(typeof value === 'string' ? value : null);
}

function normalizeTextForComparison(value: string | null | undefined): string {
  if (typeof value !== 'string') {
    return '';
  }

  return value.trim();
}

async function recordLeadTimelineEvent({
  user,
  leadId,
  eventType,
  payload,
}: RecordLeadTimelineEventArgs): Promise<void> {
  const actor = requireAdminOrManager(user);
  void actor;

  const normalizedLeadId = leadId.trim();
  if (normalizedLeadId.length === 0) {
    throw createServiceError({
      code: 'validation_failed',
      message: 'Lead identifier is required.',
    });
  }

  const preparedPayload = normalizeTimelinePayload(payload);

  const { error } = await (
    client.rpc as unknown as (
      fn: string,
      args: Record<string, unknown>,
    ) => Promise<{ data: unknown; error: PostgrestError | null }>
  )('record_lead_event', {
    p_lead_id: normalizedLeadId,
    p_event_type: eventType,
    p_payload: preparedPayload,
  });

  if (error !== null) {
    throw fromPostgrestError(error);
  }
}

async function recordLeadTimelineEventSafe(
  args: RecordLeadTimelineEventArgs,
  context: string,
): Promise<void> {
  try {
    await recordLeadTimelineEvent(args);
  } catch (error) {
    console.error(`Failed to record lead timeline event (${context})`, error);
  }
}

function handleLeadServiceError(error: unknown): never {
  if (isServiceError(error)) {
    throw error;
  }

  throw normalizeError(error);
}
